import { Injectable } from "@angular/core";
import { ApiService } from "../api/api.service";
import { HttpResponse } from "../api/http-response.interface";
import { HttpStatus } from "../api/http-status.enum";
import { ILoginPayload, IRefreshTokenBody, IRefreshTokenPayload, ISuggestedVendor } from "./account-api.interface";
import { AccountMapper } from "./account.mapper";
import { ISupplierDTO, IUpdateDisplayPreferencesDTO, IUpdateFromCompanyInfoPage } from "./current-supplier.interface";
import { CurrentUser } from "./current-user.interface";
import { InvitationDetails } from "./register.interface";
import { UserRole } from "./user-role";

@Injectable({
	providedIn: "root",
})
export class AccountApi {
	private readonly userResourceUrl = "/Users";
	private readonly supplierResourceUrl = "/Suppliers";
	private readonly invitationsResourceUrl = "/Invitations";
	private readonly customerResourceUrl = "/VendorAccount";

	constructor(private apiService: ApiService) {}

	async login(payload: ILoginPayload): Promise<HttpResponse<CurrentUser>> {
		payload.username = payload.username.toLowerCase();
		return new Promise<HttpResponse<CurrentUser>>((resolve) => {
			this.apiService.post<CurrentUser>(`${this.userResourceUrl}/Authenticate`, payload, {}, false).subscribe((res) => {
				if (res.data) {
					const data = AccountMapper.mapCurrentUserResponse(res);
					resolve(data);
				}
				resolve(res);
			});
		});
	}

	async customerLogin(payload: ILoginPayload): Promise<HttpResponse<CurrentUser>> {
		payload.username = payload.username.toLowerCase();
		return new Promise<HttpResponse<CurrentUser>>((resolve) => {
			this.apiService.post<CurrentUser>(`${this.customerResourceUrl}/LoginCustomer`, payload, {}, false).subscribe((res) => {
				if (res.data) {
					const data = AccountMapper.mapCurrentCustomerUserResponse(res);
					resolve(data);
				}
				resolve(res);
			});
		});
	}

	async remoteLogin(token: string): Promise<HttpResponse<CurrentUser>> {
		return new Promise<HttpResponse<CurrentUser>>((resolve) => {
			this.apiService.post<CurrentUser>(`${this.customerResourceUrl}/LoginCustomerRemote`, { orgillToken: token }).subscribe((res) => {
				const data = AccountMapper.mapCurrentCustomerUserResponse(res);
				resolve(data);
			});
		});
	}

	async getUserDetails(): Promise<HttpResponse<CurrentUser>> {
		return new Promise<HttpResponse<CurrentUser>>((resolve) => {
			this.apiService.get<CurrentUser>(`${this.userResourceUrl}/GetUserDetails`).subscribe((res) => {
				const data = AccountMapper.mapCurrentUserResponse(res);
				resolve(data);
			});
		});
	}

	async refreshToken(payload: IRefreshTokenPayload): Promise<HttpResponse<IRefreshTokenBody>> {
		return new Promise<HttpResponse<IRefreshTokenBody>>((resolve) => {
			this.apiService.post<IRefreshTokenBody>(`${this.userResourceUrl}/RefreshToken`, payload).subscribe((res) => {
				resolve(res);
			});
		});
	}

	async refreshCustomerToken(payload: IRefreshTokenPayload): Promise<HttpResponse<IRefreshTokenBody>> {
		return new Promise<HttpResponse<IRefreshTokenBody>>((resolve) => {
			this.apiService.post<IRefreshTokenBody>(`${this.customerResourceUrl}/RefreshToken`, payload).subscribe((res) => {
				resolve(res);
			});
		});
	}

	async resetForgotPassword(token: string, password: string): Promise<HttpResponse<null>> {
		const body = {
			token: token,
			password: password,
		};
		return new Promise<HttpResponse<null>>((resolve) => {
			this.apiService.post(`${this.userResourceUrl}/ResetForgotPassword`, body).subscribe((res) => {
				resolve(res);
			});
		});
	}

	async getCurrentSupplierById(supplierId: string): Promise<HttpResponse<ISupplierDTO>> {
		return new Promise<HttpResponse<ISupplierDTO>>((resolve) => {
			this.apiService.get<ISupplierDTO>(`${this.supplierResourceUrl}/GetById/${supplierId}`).subscribe((res) => {
				resolve(res);
			});
		});
	}

	async checkUsernameUniqueness(username: string): Promise<HttpResponse<boolean>> {
		return new Promise<HttpResponse<boolean>>((resolve) => {
			this.apiService.get(`${this.userResourceUrl}/UsernameAlreadyExists/` + username).subscribe((res) => {
				resolve(res);
			});
		});
	}

	async getRegisterLinkData(data: string): Promise<HttpResponse<InvitationDetails>> {
		return new Promise<HttpResponse<InvitationDetails>>((resolve) => {
			this.apiService
				.get<InvitationDetails>(`${this.invitationsResourceUrl}/GetRegisterLinkData?data=` + encodeURIComponent(data), {}, false)
				.subscribe((res) => {
					resolve(res);
				});
		});
	}

	async createUser(user: CurrentUser, registerLinkData: string): Promise<HttpResponse<CurrentUser>> {
		const isPrimaryRole = user.role === UserRole.PRIMARY || user.role === UserRole.NEW_PRIMARY;

		const userPayload = {
			...user,
			username: user.username.toLowerCase(),
			role: this.convertTransferRoleToNormal(user.role),
			data: registerLinkData,
			...(isPrimaryRole ? { termsAccepted: true } : {}),
		};
		return new Promise<HttpResponse<CurrentUser>>((resolve) => {
			this.apiService.post<CurrentUser>(`${this.userResourceUrl}/register?isTransferAccount=${this.isTransfer(user.role)}`, userPayload).subscribe((res) => {
				if (res?.status === HttpStatus.SUCCESS && res?.data) {
					const data = AccountMapper.mapCurrentUserResponse(res);
					resolve(data);
					return;
				}
				resolve(res);
			});
		});
	}

	async updateSupplier(supplier: ISupplierDTO | IUpdateFromCompanyInfoPage): Promise<HttpResponse<null>> {
		return new Promise<HttpResponse<null>>((resolve) => {
			this.apiService.put<null>(`${this.supplierResourceUrl}/UpdateCompanyInformation`, supplier).subscribe((res) => {
				resolve(res);
			});
		});
	}

	async updateSupplierDisplayPreferences(displayPref: IUpdateDisplayPreferencesDTO): Promise<
		HttpResponse<{
			itemsAffected: number;
		}>
	> {
		return new Promise<HttpResponse<{ itemsAffected: number }>>((resolve) => {
			this.apiService.put<{ itemsAffected: number }>(`${this.supplierResourceUrl}/UpdateDisplayPreferences`, displayPref).subscribe((res) => {
				resolve(res);
			});
		});
	}

	async sendResetPasswordEmail(email: string): Promise<HttpResponse<null>> {
		return new Promise<HttpResponse<null>>((resolve) => {
			this.apiService.post<null>(`${this.userResourceUrl}/SendForgotPasswordEmail/` + email, {}).subscribe((res) => {
				resolve(res);
			});
		});
	}

	async sendConfirmationEmail(): Promise<HttpResponse<null>> {
		return new Promise<HttpResponse<null>>((resolve) => {
			this.apiService.post<null>(`${this.userResourceUrl}/SendConfirmationEmail`, {}).subscribe((res) => {
				resolve(res);
			});
		});
	}

	async getSuggestedVendors(supplierId: string): Promise<HttpResponse<ISuggestedVendor[]>> {
		return new Promise<HttpResponse<ISuggestedVendor[]>>((resolve) => {
			this.apiService.get<ISuggestedVendor[]>(`${this.supplierResourceUrl}/GetSuggestedVendors/${supplierId}`).subscribe((res) => {
				resolve(res);
			});
		});
	}

	private isTransfer(role: UserRole): boolean {
		return role === UserRole.NEW_PRIMARY || role === UserRole.NEW_PROP65;
	}

	private convertTransferRoleToNormal(role: UserRole): UserRole {
		switch (role) {
			case UserRole.NEW_PRIMARY:
				return UserRole.PRIMARY;
			case UserRole.NEW_PROP65:
				return UserRole.PROP65;
			default:
				return role;
		}
	}
}
